/**
 * Model health, moved from the retired TierModelsPanel: the llm_call_log
 * rollup plus the per-process sick memo.
 */

import { useCallback, useEffect, useState } from "react";

interface ModelHealthStatRow {
  provider: string;
  model: string;
  attempts: number;
  successes: number;
  errors: number;
  empties: number;
  successRate: number;
  medianDurationMs: number | null;
  p95DurationMs: number | null;
  lastSeenAt: string | null;
  topErrorStatuses: Array<{ status: number | null; count: number }>;
}

interface ModelHealthResponse {
  windowMinutes: number;
  stats: ModelHealthStatRow[];
  memo: {
    process: string;
    entries: Array<{ model: string; state: string; sickForMs: number; probeInFlight: boolean; cooldownRemainingMs?: number }>;
  };
  degraded: boolean;
  writer?: { pending: number; dropped: number; failures: number; capacity: number };
}

/** Success-rate color: amber < 90%, error-red < 75%, the incident thresholds
 * the panel's health card uses for a one-glance read. */
function healthRateColor(rate: number): string {
  if (rate < 0.75) return "var(--error)";
  if (rate < 0.9) return "var(--warning)";
  return "var(--success)";
}

function formatDuration(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms)) return "—";
  if (ms < 1_000) return `${Math.round(ms)}ms`;
  return `${(ms / 1_000).toFixed(1)}s`;
}

/** Model health (last 30 min): the llm_call_log rollup + the per-process
 * sick memo, refreshed alongside the panel. Self-contained: a fetch failure
 * renders the degraded state, never breaks the panel. */
export function ModelHealthCard() {
  const [health, setHealth] = useState<ModelHealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refreshHealth = useCallback(async () => {
    try {
      const res = await fetch("/api/admin/tier-models/health");
      if (!res.ok) {
        setError(`Request failed (${res.status})`);
        return;
      }
      setHealth((await res.json()) as ModelHealthResponse);
      setError(null);
    } catch {
      setError("Unable to load model health.");
    }
  }, []);

  useEffect(() => {
    void refreshHealth();
    const interval = window.setInterval(() => void refreshHealth(), 30_000);
    return () => window.clearInterval(interval);
  }, [refreshHealth]);

  return (
    <div
      className="rounded-[var(--radius-card)] border px-4 py-3"
      style={{
        borderColor: "var(--print-border)",
        background: "var(--print-bg)",
        boxShadow: "var(--shadow-print)",
      }}
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
          Model health
        </div>
        <button
          type="button"
          className="text-xs underline"
          style={{ color: "var(--accent)" }}
          onClick={() => void refreshHealth()}
        >
          Refresh
        </button>
      </div>
      <div className="text-xs mb-3" style={{ color: "var(--text-secondary)" }}>
        Success rate per (provider, model) over the last{" "}
        {health?.windowMinutes ?? 30} minutes, from the llm_call_log call record,
        every dispatch attempt, failed ones included. Amber below 90%, red below 75%.
        The sick-memo section reports THIS app process only.
      </div>
      {error && (
        <div className="text-xs" style={{ color: "var(--error)" }}>
          {error}
        </div>
      )}
      {health?.writer && (health.writer.dropped > 0 || health.writer.failures > 0) && (
        <div className="text-xs mb-2" role="status" style={{ color: "var(--warning)" }}>
          Health history is incomplete: {health.writer.dropped} records dropped and {health.writer.failures} writes failed since this process started.
        </div>
      )}
      {health?.degraded && (
        <div className="text-xs mb-2" style={{ color: "var(--warning)" }}>
          Stats are degraded. The call log is cold or unreachable. The memo below
          is still live.
        </div>
      )}
      {health && health.stats.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ color: "var(--text-tertiary)" }}>
                <th className="text-left font-normal py-1">Provider</th>
                <th className="text-left font-normal py-1">Model</th>
                <th className="text-right font-normal py-1">Success</th>
                <th className="text-right font-normal py-1">Attempts</th>
                <th className="text-right font-normal py-1">Median</th>
                <th className="text-left font-normal py-1 pl-4">Top errors</th>
              </tr>
            </thead>
            <tbody>
              {health.stats.map((s) => (
                <tr key={`${s.provider}:${s.model}`} style={{ borderTop: "1px solid var(--border-subtle)" }}>
                  <td className="py-1" style={{ color: "var(--text-secondary)" }}>{s.provider}</td>
                  <td
                    className="py-1"
                    style={{ color: "var(--text-primary)", fontFamily: "var(--font-mono)" }}
                  >
                    {s.model}
                  </td>
                  <td
                    className="py-1 text-right"
                    style={{ color: healthRateColor(s.successRate), fontFamily: "var(--font-mono)" }}
                  >
                    {(s.successRate * 100).toFixed(1)}%
                  </td>
                  <td className="py-1 text-right" style={{ color: "var(--text-secondary)" }}>
                    {s.attempts}
                  </td>
                  <td className="py-1 text-right" style={{ color: "var(--text-secondary)" }}>
                    {formatDuration(s.medianDurationMs)}
                  </td>
                  <td className="py-1 pl-4" style={{ color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>
                    {s.topErrorStatuses
                      .map((e) => `${e.status ?? "err"}×${e.count}`)
                      .join("  ") || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {health && health.stats.length === 0 && !error && (
        <div className="text-xs" style={{ color: "var(--text-tertiary)" }}>
          No calls in the window yet.
        </div>
      )}
      {health && health.memo.entries.length > 0 && (
        <div className="mt-3 text-xs">
          <div className="mb-1 font-semibold" style={{ color: "var(--text-secondary)" }}>
            Sick memo (this process)
          </div>
          {health.memo.entries.map((m) => (
            <div
              key={m.model}
              className="font-mono"
              style={{ color: m.state === "sick" ? "var(--error)" : "var(--warning)" }}
            >
              {m.model}: {m.state} for {Math.round(m.sickForMs / 1000)}s
              {m.state === "throttled" && typeof m.cooldownRemainingMs === "number"
                ? `, ${Math.ceil(m.cooldownRemainingMs / 1000)}s of cooldown left`
                : ""}
              {m.probeInFlight ? " (probe in flight)" : ""}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
